<script setup lang="ts">
// ================================
// « POINTS À VÉRIFIER » (contrat de l'interface v1.2 §6 rangée 4 ; maquette
// 2d états P1 à P5 ; décisions A1 et A7 ; DEV-2311, lot 4b).
//
// Ce que le courtier doit vivre : la rangée ne liste que ce qui demande un
// geste avant d'envoyer, une ligne par action, au même format court que la
// carte de tête, avec « Corriger » vers le champ ; le texte complet derrière
// « Voir le détail » seulement quand il ajoute le repli chiffré. Les constats
// (qualité des mesures) vivent en gris dans « Hypothèses de l'étude ›
// Mesures » ; la réserve du palier C5 n'est jamais ici (A7).
// ================================
import { ref } from "vue"
import { NIVEAU_3 } from "@modules/courtage/utils/optim-turpe-v2.boutons"
import type { AncreAffiner } from "@modules/courtage/utils/optim-turpe-v2.completer"
import type { PointDAction } from "@modules/courtage/utils/optim-turpe-v2.points"

type Props = {
    /** Les actions de l'étude affichée, le raccordement d'abord. */
    points: PointDAction[]
    /** Les codes des points dépliés à l'ouverture (relecture, story P3). */
    detailsOuverts?: string[]
}
const props = withDefaults(defineProps<Props>(), {
    detailsOuverts: () => [],
})
const emit = defineEmits<{ corriger: [ancre: AncreAffiner] }>()

/** Les codes des points dont le détail est déplié (aucun au repos). */
const deplies = ref<string[]>([...props.detailsOuverts])

/** « Voir le détail » / « Masquer le détail » d'un point. */
function onBasculer(code: string): void {
    deplies.value = deplies.value.includes(code)
        ? deplies.value.filter((c) => c !== code)
        : [...deplies.value, code]
}
</script>

<template>
    <ul
        v-if="points.length > 0"
        class="optim-turpe-v2-points-a-verifier flex flex-col gap-2 text-sm"
        data-testid="optim-turpe-v2-points-a-verifier"
    >
        <li
            v-for="point in points"
            :key="point.code"
            class="flex min-w-0 items-start gap-2"
            data-testid="optim-turpe-v2-reserve-ligne"
            :data-code="point.code"
        >
            <span
                class="mt-1.5 size-2 shrink-0 rounded-full bg-[var(--p-orange-500)]"
                aria-hidden="true"
            />
            <div class="flex min-w-0 flex-col items-start gap-1">
                <p
                    class="min-w-0 text-[var(--p-orange-700)]"
                    data-testid="optim-turpe-v2-point-constat"
                >
                    {{ point.court }}
                </p>
                <div class="flex flex-wrap items-center gap-x-4">
                    <BaseButton
                        v-if="point.ancre"
                        v-bind="NIVEAU_3"
                        label="Corriger"
                        data-testid="optim-turpe-v2-point-corriger"
                        @click="emit('corriger', point.ancre)"
                    />
                    <BaseButton
                        v-if="point.detail"
                        v-bind="NIVEAU_3"
                        :label="
                            deplies.includes(point.code)
                                ? 'Masquer le détail'
                                : 'Voir le détail'
                        "
                        :aria-expanded="deplies.includes(point.code)"
                        data-testid="optim-turpe-v2-point-voir-detail"
                        @click="onBasculer(point.code)"
                    />
                </div>
                <p
                    v-if="point.detail && deplies.includes(point.code)"
                    class="min-w-0 text-surface-700"
                    data-testid="optim-turpe-v2-point-detail"
                >
                    {{ point.detail }}
                </p>
            </div>
        </li>
    </ul>
</template>
